import { Injectable, Injector, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ErrorMessageResponse } from '@qtek/core/websockets-core';
import { Message } from '@qtek/shared/models';

import * as fromAlertDialogComponent from './components/alert-dialog/alert-dialog.component';
import { WsErrorSuppressionService } from './ws-error-suppression.service';
import { matchesWsError, WsErrorRule } from './ws-error-rules';
import { WsErrorRulesRegistry } from './ws-error-rules.registry';

@Injectable({
  providedIn: 'root',
})
export class WsErrorHandlerService {
  private matDialog = inject(MatDialog);
  private injector = inject(Injector);
  private registry = inject(WsErrorRulesRegistry);
  private readonly builtInRules: WsErrorRule[] = [
    // SD-2450: a failed Allo turn is already surfaced inline as an interrupted
    // bubble (from a `chatStream` error event), so skip the redundant generic
    // dialog for the same failed `chatEvent`/`ins` operation.
    {
      match: (message, injector) =>
        matchesWsError(message, { ent: 'ChatEvent', op: 'ins' }) &&
        injector
          .get(WsErrorSuppressionService)
          .consumeChatEventErrorSuppression(),
    },
  ];

  getErrorHandler() {
    return this.errorsHandler;
  }

  public errorsHandler(message: ErrorMessageResponse) {
    if (message.sts && message.sts.sts === 'ERR') {
      const rule =
        this.builtInRules.find(r => r.match(message, this.injector)) ??
        this.registry.find(message, this.injector);
      if (rule) {
        rule.handle?.(message, this.injector);
        return;
      }

      const { sts } = message;
      const errors = Object.keys(sts && sts.attrs ? sts.attrs : {}).map(
        key => sts.attrs[key]
      );
      const main = errors.find(error => error.main);
      const details = errors.filter(error => !error.main);

      this.matDialog.open(fromAlertDialogComponent.WSAlertDialogComponent, {
        ...fromAlertDialogComponent.wsAlertDialogConfig,
        data: {
          message: new Message(
            main.msg ?? `ERR_DEFAULT_MSG_WITH_PARAMS`,
            main.prms ?? [
              Object.keys(sts && sts.attrs ? sts.attrs : {}).join(', '),
            ]
          ),
          details: details.map(detail => new Message(detail.msg, detail.prms)),
        },
      });
    }
  }
}
